import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Users, Briefcase, Sparkles, CheckCircle, Star, Lightbulb } from 'lucide-react';
import SubmissionPopup from '../components/SubmissionPopup';
import MouseFollowEffect from '../components/MouseFollowEffect';
import QuestionNavigator from '../components/QuestionNavigator';

interface FormData {
  name: string;
  email: string;
  company: string;
  answers: Record<string, string | string[]>;
}

export default function LuminousTalent() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    answers: {}
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const formContainerRef = useRef<HTMLDivElement>(null);

  const questions = [
    {
      id: 'clear_goals',
      question: 'Rebecca, have you established clear goals for your talent acquisition and executive search processes at Luminous Talent?',
      type: 'select',
      options: [
        'Yes, we have well-defined goals for both areas',
        'We have goals for talent acquisition but not executive search',
        'We have goals for executive search but not talent acquisition',
        'No, we need to establish clearer goals',
        'We\'re in the process of redefining our goals'
      ]
    },
    {
      id: 'specific_outcomes',
      question: 'Are you focusing on achieving specific outcomes in these areas at Luminous Talent?',
      type: 'select',
      options: [
        'Yes, we have specific metrics we\'re targeting',
        'We have some metrics but need more clarity',
        'We\'re more focused on process than outcomes currently',
        'No, we need to define better outcome metrics',
        'We\'re transitioning to a more outcome-focused approach'
      ]
    },
    {
      id: 'timeline',
      question: 'Are you looking to address these challenges within the next 30 days?',
      type: 'select',
      options: [
        'Yes, this is an immediate priority',
        'Within 60 days is our target',
        'We\'re planning for next quarter',
        'We have a 6-month timeline',
        'We\'re still in the planning phase'
      ]
    },
    {
      id: 'current_challenges',
      question: 'What significant challenges is Luminous Talent currently facing in your recruitment or executive search processes?',
      type: 'multiselect',
      options: [
        'Candidate sourcing difficulties',
        'High time-to-fill positions',
        'Inconsistent placement performance',
        'Client acquisition challenges',
        'Process inefficiencies',
        'Technology limitations',
        'Team skill gaps',
        'Market competition',
        'Client retention issues',
        'Candidate experience issues',
        'Other challenges'
      ]
    },
    {
      id: 'automation_tools',
      question: 'Is Luminous Talent utilizing any automation tools to enhance revenue or streamline operations?',
      type: 'select',
      options: [
        'Yes, we have comprehensive automation systems',
        'We use some automation but need more',
        'We\'ve just started implementing automation',
        'No, but we\'re exploring options',
        'No, we rely primarily on manual processes'
      ]
    },
    {
      id: 'recruitment_impact',
      question: 'Do you believe improving Luminous Talent\'s recruitment process would create a meaningful impact on your business?',
      type: 'select',
      options: [
        'Yes, it would significantly impact our business',
        'It would have a moderate impact',
        'It would help but other areas are higher priority',
        'I\'m unsure of the potential impact',
        'No, other areas would drive more value'
      ]
    },
    {
      id: 'kpis',
      question: 'Has Luminous Talent defined key performance indicators (KPIs) to measure success in talent acquisition and executive search?',
      type: 'select',
      options: [
        'Yes, we have comprehensive KPIs for both areas',
        'We have KPIs for talent acquisition but not executive search',
        'We have KPIs for executive search but not talent acquisition',
        'We have basic metrics but need more robust KPIs',
        'No, we need to establish proper KPIs'
      ]
    },
    {
      id: 'budget',
      question: 'If we can provide solutions that help you achieve these goals and drive meaningful results, what budget would you be willing to invest?',
      type: 'select',
      options: [
        '$5,000 - $10,000',
        '$10,000 - $15,000',
        '$15,000 - $20,000',
        '$20,000+'
      ]
    }
  ];

  // Ensure form container is visible when changing steps
  React.useEffect(() => {
    if (currentStep > 0 && formContainerRef.current) {
      formContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  const handleInputChange = (questionId: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: value
      }
    }));
  };

  const handleBasicInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNoteChange = (questionId: string, note: string) => {
    setNotes(prev => ({
      ...prev,
      [questionId]: note
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleMultiSelectChange = (questionId: string, option: string) => {
    setFormData(prev => {
      const currentSelections = Array.isArray(prev.answers[questionId]) 
        ? prev.answers[questionId] as string[] 
        : [];
      
      const updatedSelections = currentSelections.includes(option)
        ? currentSelections.filter(item => item !== option)
        : [...currentSelections, option];
      
      return {
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: updatedSelections
        }
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://hook.eu2.make.com/3erm7e8qo1wvav7lba5sdsio3v545xc6', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'luminous_talent',
          timestamp: new Date().toISOString(),
          name: formData.name,
          email: formData.email,
          company: formData.company,
          answers: formData.answers,
          notes: notes
        }),
      });
      
      if (response.ok) {
        setShowPopup(true);
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          company: '',
          answers: {}
        });
        setNotes({});
        setCurrentStep(0);
      } else {
        console.error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Star className="w-16 h-16 text-golden animate-pulse" />
              <Sparkles className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Luminous <span className="text-golden">Talent</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Personalized for Rebecca Rizzio, CEO of Luminous Talent
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              We're excited to learn more about Luminous Talent to deliver a tailored approach that drives meaningful results for your executive search and talent acquisition business.
            </p>
          </div>
        </motion.div>

        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto" ref={formContainerRef}>
          <QuestionNavigator isActive={currentStep === 0}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-2xl font-bold mb-6 text-golden">Let's Get Started</h2>
              <p className="text-gray-300 mb-8">
                Please provide some basic information before we dive into the specific questions about Luminous Talent's recruitment and executive search processes.
              </p>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Your Name <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="Rebecca Rizzio"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address <span className="text-golden">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="rebecca@luminous-talent.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    Company Name <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="Luminous Talent"
                  />
                </div>
                
                <div className="pt-4">
                  <button
                    onClick={handleNext}
                    disabled={!formData.name || !formData.email || !formData.company}
                    className="w-full px-6 py-4 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Questions
                  </button>
                </div>
              </div>
            </motion.div>
          </QuestionNavigator>

          {questions.map((question, index) => {
            const stepNumber = index + 1;
            return (
              <QuestionNavigator 
                key={question.id} 
                isActive={currentStep === stepNumber}
              >
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">
                      Question {stepNumber} of {questions.length}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold">
                    {question.question}
                  </h3>
                  
                  {question.type === 'select' && (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 pb-2">
                      {question.options.map((option) => (
                        <div 
                          key={option}
                          onClick={() => handleInputChange(question.id, option)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                            formData.answers[question.id] === option
                              ? 'border-golden bg-golden/10'
                              : 'border-gray-800 bg-black/30 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                              formData.answers[question.id] === option
                                ? 'border-golden'
                                : 'border-gray-600'
                            }`}>
                              {formData.answers[question.id] === option && (
                                <div className="w-3 h-3 rounded-full bg-golden" />
                              )}
                            </div>
                            <span className="text-gray-300">{option}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {question.type === 'multiselect' && (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 pb-2">
                      {question.options.map((option) => {
                        const isSelected = Array.isArray(formData.answers[question.id]) && 
                          (formData.answers[question.id] as string[]).includes(option);
                        
                        return (
                          <div 
                            key={option}
                            onClick={() => handleMultiSelectChange(question.id, option)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                              isSelected
                                ? 'border-golden bg-golden/10'
                                : 'border-gray-800 bg-black/30 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-5 h-5 rounded-md border-2 mr-3 flex items-center justify-center ${
                                isSelected
                                  ? 'border-golden'
                                  : 'border-gray-600'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-golden" />
                                )}
                              </div>
                              <span className="text-gray-300">{option}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="pt-4 flex justify-between">
                    <button
                      onClick={handlePrevious}
                      className="px-6 py-3 border-2 border-gray-700 text-gray-300 rounded-full font-medium hover:border-golden hover:text-golden transition-all duration-300"
                    >
                      Previous
                    </button>
                    
                    {stepNumber < questions.length ? (
                      <button
                        onClick={handleNext}
                        disabled={!formData.answers[question.id] || 
                          (Array.isArray(formData.answers[question.id]) && 
                          (formData.answers[question.id] as string[]).length === 0)}
                        className="px-6 py-3 bg-golden text-white rounded-full font-medium hover:bg-golden-600 transition-all duration-300 disabled:opacity-70"
                      >
                        Next Question
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-6 py-3 bg-golden text-white rounded-full font-medium hover:bg-golden-600 transition-all duration-300 disabled:opacity-70"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Responses'}
                      </button>
                    )}
                  </div>
                </div>
              </QuestionNavigator>
            );
          })}

          <QuestionNavigator isActive={currentStep > questions.length}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="text-center py-8"
            >
              <CheckCircle className="w-16 h-16 text-golden mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Thank you Rebecca!</h2>
              <p className="text-gray-300 mb-6">
                Your responses have been submitted successfully. You will be redirected to the scheduling page for our meeting shortly.
              </p>
            </motion.div>
          </QuestionNavigator>
        </MouseFollowEffect>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-r from-blue-900/30 to-golden/20 rounded-2xl p-8 border border-golden/20">
            <h2 className="text-2xl font-bold mb-4">About Luminous Talent</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Here's what we found on Luminous Talent</h3>
                <p className="text-gray-300">
                  A premier executive search and talent acquisition firm specializing in connecting exceptional talent with forward-thinking organizations across various industries.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Strategic Approach</h3>
                <p className="text-gray-300">
                  Luminous Talent employs a consultative approach to understand each client's unique needs and culture, ensuring precise matches that drive organizational success.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Industry Expertise</h3>
                <p className="text-gray-300">
                  With deep expertise across technology, healthcare, finance, and other key sectors, Luminous Talent provides specialized recruitment solutions tailored to each industry's unique challenges.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Client-Centered Philosophy</h3>
                <p className="text-gray-300">
                  Luminous Talent builds lasting partnerships with clients through transparent communication, personalized service, and a commitment to delivering exceptional talent that drives business growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <SubmissionPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        message="Thank you Rebecca! You will be redirected to schedule your meeting shortly."
        redirectUrl="https://cal.com/dsmarketing.meeting.with.pascoal/luminous-talent-ds-marketing-1st-talk"
        isLeo={false}
      />
    </div>
  );
}